import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
    Wine, Percent, Users, Inbox, ArrowLeft, LogOut,
    Star, UploadCloud, GitMerge, ListTodo, Settings,
    PlugZap, Database, KeyRound, HardDrive, Snowflake, Sparkles, ShieldOff,
    Map as MapIcon, Printer, BookMarked, EuroIcon, Globe, AlertCircle,
    ShieldQuestion, Camera, GlassWater,
} from "lucide-react";

const NAV = [
    { to: "/admin/vini", label: "Vini", icon: Wine },
    { to: "/admin/distillati", label: "Distillati", icon: GlassWater },
    { to: "/admin/aggiungi-foto", label: "Aggiungi da foto", icon: Camera },
    { to: "/admin/in-evidenza", label: "In Evidenza", icon: Star },
    { to: "/admin/imports", label: "Import PDF/CSV", icon: UploadCloud },
    { to: "/admin/mexal", label: "Mexal Live", icon: PlugZap },
    { to: "/admin/senza-prezzo", label: "Senza prezzo", icon: EuroIcon },
    { to: "/admin/siti-produttore", label: "Siti produttore", icon: Globe },
    { to: "/admin/mismatch-produttori", label: "Mismatch producer", icon: AlertCircle },
    // Fase 8.3 · hub unificato Segnalazioni (Duplicati + Incoerenze + AI in attesa)
    { to: "/admin/segnalazioni", label: "Segnalazioni", icon: ShieldQuestion, badgeKey: "review_total" },
    { to: "/admin/data-quality", label: "Qualità dati", icon: ShieldQuestion, badgeKey: "dq_open" },
    { to: "/admin/mexal-import", label: "Import Mexal", icon: ListTodo },
    { to: "/admin/mexal-csv", label: "Mexal CSV", icon: Database },
    { to: "/admin/proposte-merge", label: "Merge", icon: GitMerge },
    { to: "/admin/code", label: "Code", icon: ListTodo },
    { to: "/admin/prezzi", label: "Prezzi", icon: Percent },
    { to: "/admin/sync-esterno", label: "Sync Esterno", icon: KeyRound },
    { to: "/admin/backup", label: "Backup", icon: HardDrive },
    { to: "/admin/sommelier", label: "Sommelier AI", icon: Sparkles },
    { to: "/admin/mappa", label: "Zone mappa", icon: MapIcon },
    { to: "/admin/sezioni-carta", label: "Sezioni carta", icon: BookMarked },
    { to: "/admin/stampa-carta", label: "Stampa carta", icon: Printer },
    { to: "/admin/configurazione", label: "Config", icon: Settings },
    { to: "/admin/utenti", label: "Utenti", icon: Users },
    { to: "/admin/gdpr", label: "GDPR", icon: ShieldOff },
    { to: "/admin/outbox", label: "Outbox", icon: Inbox },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [freeze, setFreeze] = useState(false);
    const [badges, setBadges] = useState({ dq_open: 0, review_total: 0 });

    useEffect(() => {
        const load = () => api.get("/admin/config").then(({ data }) => setFreeze(!!data.freeze)).catch(() => {});
        load();
        const h = setInterval(load, 10000);
        return () => clearInterval(h);
    }, []);

    // Fase 6.9 · badge segnalazioni qualità dati aperte + Fase 8.3 · totale
    // segnalazioni (duplicati + incoerenze + AI in attesa).
    useEffect(() => {
        const loadBadges = () => Promise.all([
            api.get("/admin/data-quality/count").catch(() => ({ data: { open: 0 } })),
            api.get("/admin/review-counts").catch(() => ({ data: { total: 0 } })),
        ]).then(([dq, review]) => {
            setBadges({
                dq_open: Number(dq.data?.open || 0),
                review_total: Number(review.data?.total || 0),
            });
        });
        loadBadges();
        const h = setInterval(loadBadges, 60_000);
        return () => clearInterval(h);
    }, []);

    return (
        <div className="min-h-screen bg-[#F5F5F7]" data-testid="admin-layout">
            {freeze && (
                <div className="bg-blue-100 border-b border-blue-300 text-blue-900 py-2 px-6 flex items-center gap-3" data-testid="freeze-banner">
                    <Snowflake className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                    <span className="text-sm">
                        <strong>Modalità Freeze attiva.</strong> Sync automatici e AI sospesi.
                        Le modifiche manuali admin sono ancora consentite.
                    </span>
                </div>
            )}
            <header className="bg-white border-b border-[hsl(var(--border))]">
                <div className="px-6 flex items-center justify-between h-14">
                    <div className="flex items-center gap-6">
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                            data-testid="admin-back-to-catalog"
                        >
                            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                            Torna alla home
                        </button>
                        <span className="hidden md:inline-block h-5 w-px bg-[hsl(var(--border))]" />
                        <span className="text-lg tracking-widest uppercase text-[hsl(var(--primary))] hidden md:inline-block">
                            Orterie · Admin
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-[hsl(var(--muted-foreground))] hidden sm:inline">{user?.email}</span>
                        <button
                            type="button"
                            onClick={() => { logout(); navigate("/"); }}
                            className="co-btn-ghost text-xs"
                            data-testid="admin-logout"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Esci
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex flex-col md:flex-row min-h-[calc(100vh-56px)]">
                <aside className="md:w-56 md:border-r border-b md:border-b-0 border-[hsl(var(--border))] bg-white">
                    <nav className="md:sticky md:top-14 flex md:flex-col overflow-x-auto md:overflow-visible">
                        {NAV.map(({ to, label, icon: Icon, badgeKey }) => {
                            const badgeValue = badgeKey ? badges[badgeKey] : 0;
                            return (
                                <NavLink
                                    key={to}
                                    to={to}
                                    data-testid={`admin-nav-${label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-5 py-3 text-sm border-l-2 whitespace-nowrap transition-colors ${
                                            isActive
                                                ? "border-[hsl(var(--primary))] bg-[#FDFBF7] text-[hsl(var(--primary))]"
                                                : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                                        }`
                                    }
                                >
                                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                                    <span className="flex-1">{label}</span>
                                    {badgeValue > 0 && (
                                        <span
                                            className="ml-auto inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] font-semibold"
                                            data-testid={`admin-nav-badge-${badgeKey}`}
                                        >
                                            {badgeValue > 99 ? "99+" : badgeValue}
                                        </span>
                                    )}
                                </NavLink>
                            );
                        })}
                    </nav>
                </aside>

                <main className="flex-1 p-5 sm:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
